import React from 'react';
import { MapPin, Navigation, Clock, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const Location = () => {
  const { t } = useLanguage();
  const locations = [
    {
      name: t('location_gym_boivin'),
      address: '1 rue Marius Chanteur, 21000 Dijon',
      days: t('location_days_fri_sat'),
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2712.5!2d5.0445!3d47.3247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s1%20Rue%20Marius%20Chanteur%2C%2021000%20Dijon!5e0!3m2!1sfr!2sfr!4v1234567890',
      tramLines: 'T1',
      busLines: 'L3'
    }
  ];

  return (
    <section id="location" className="py-20 bg-zinc-900">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('location_title')}
            </h2>
            <div className="w-24 h-1 bg-red-500 mx-auto mb-6"></div>
            <p className="text-gray-400 text-lg">
              {t('location_subtitle')}
            </p>
          </div>

          {/* Locations */}
          <div className="space-y-12">
            {locations.map((location, index) => (
              <div key={index} className="grid lg:grid-cols-2 gap-8">
                {/* Map */}
                <div className="rounded-lg overflow-hidden border-2 border-zinc-700 hover:border-red-500 transition-colors duration-300">
                  <iframe
                    src={location.mapUrl}
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Google Maps - ${location.name}`}
                  ></iframe>
                </div>

                {/* Location Details */}
                <div className="space-y-6">
                  <Card className="bg-zinc-800/50 backdrop-blur-sm border-zinc-700 p-6">
                    <div className="flex items-start gap-4">
                      <div className="text-red-500 mt-1">
                        <Calendar size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">{location.name}</h3>
                        <p className="text-red-500 font-semibold mb-3">{location.days}</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-zinc-800/50 backdrop-blur-sm border-zinc-700 p-6">
                    <div className="flex items-start gap-4">
                      <div className="text-red-500 mt-1">
                        <MapPin size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('location_address')}</h3>
                        <p className="text-gray-300 text-lg leading-relaxed">
                          {location.address}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-zinc-800/50 backdrop-blur-sm border-zinc-700 p-6">
                    <div className="flex items-start gap-4">
                      <div className="text-red-500 mt-1">
                        <Navigation size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-3">{t('location_access')}</h3>
                        <div className="space-y-2 text-gray-300">
                          <p><span className="font-semibold text-white">{t('location_tram')}</span> {location.tramLines}</p>
                          {location.busLines && (
                            <p><span className="font-semibold text-white">{t('location_bus')}</span> {location.busLines}</p>
                          )}
                          <p><span className="font-semibold text-white">{t('location_car')}</span> {t('location_parking')}</p>
                        </div>
                        <a
                          href={`https://www.google.com/maps/dir//${encodeURIComponent(location.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-4 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg transition-colors duration-300 font-medium"
                        >
                          {t('location_get_directions')}
                        </a>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </div>

          {/* Global Hours */}
          <Card className="bg-zinc-800/50 backdrop-blur-sm border-zinc-700 p-8 mt-12">
            <div className="flex items-start gap-4">
              <div className="text-red-500 mt-1">
                <Clock size={32} />
              </div>
              <div className="w-full">
                <h3 className="text-2xl font-bold text-white mb-6">{t('location_hours_title')}</h3>
                <div className="space-y-4">
                  {/* Date and Location */}
                  <div className="mb-4">
                    <p className="font-semibold text-white text-xl mb-2">{t('location_hours_saturday')}</p>
                    <p className="text-gray-400 flex items-center gap-2">
                      <MapPin size={16} />
                      {t('location_gym_boivin')}, 1 rue Marius Chanteur, 21000 Dijon
                    </p>
                  </div>
                  
                  {/* Schedule */}
                  <div className="text-gray-300 pl-4 border-l-2 border-red-500">
                    <div>
                      <p className="text-white font-medium">14h00 - 20h00</p>
                      <p className="text-sm text-gray-400">BOTY Next Generation</p>
                      <p className="text-xs text-gray-500 mt-1">Battle of the Year - Compétition officielle</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Location;
